import { useEffect, useRef } from 'react'
import { createEngineeringRenderer } from './engineering/render'
import './EngineeringBackdrop.css'

/** Local procedural artwork; animation pauses off-screen and respects reduced motion. */
export function EngineeringBackdrop() {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const drawEngineering = createEngineeringRenderer()
    const motion = matchMedia('(prefers-reduced-motion: reduce)')
    let width = 0, height = 0, frame = 0, last = 0, elapsed = 0
    let visible = true
    let color = getComputedStyle(canvas).color
    function render() {
      if (!width || !height) return
      drawEngineering(ctx!, width, height, motion.matches ? 0 : elapsed, color)
    }
    function animate(now: number) {
      if (now - last >= 1000 / 30) {
        elapsed += last ? Math.min(now - last, 100) / 1000 : 0
        last = now
        render()
      }
      frame = requestAnimationFrame(animate)
    }
    function playback() {
      cancelAnimationFrame(frame)
      last = 0
      if (!visible || document.hidden) return
      render()
      if (!motion.matches) frame = requestAnimationFrame(animate)
    }
    const resize = new ResizeObserver(() => {
      width = canvas.clientWidth
      height = canvas.clientHeight
      const ratio = Math.min(devicePixelRatio || 1, 2)
      canvas.width = Math.round(width * ratio)
      canvas.height = Math.round(height * ratio)
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
      playback()
    })
    const intersection = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      playback()
    })
    const theme = new MutationObserver(() => {
      color = getComputedStyle(canvas).color
      if (visible && !document.hidden) render()
    })
    resize.observe(canvas)
    intersection.observe(canvas)
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    motion.addEventListener('change', playback)
    document.addEventListener('visibilitychange', playback)
    return () => {
      cancelAnimationFrame(frame)
      resize.disconnect()
      intersection.disconnect()
      theme.disconnect()
      motion.removeEventListener('change', playback)
      document.removeEventListener('visibilitychange', playback)
    }
  }, [])
  return <canvas ref={ref} className="engineering-backdrop" aria-hidden="true" />
}
